// ─── Mon permis : parcours lancé par l'auto-école à l'obtention du permis (calculs purs) ───────────

export type PermisDocument = { docType: string; label: string };

export type SurveyDefinition = {
  version: string;
  ratings: { key: string; label: string }[];
  recommend: { key: string; label: string };
  comment: { key: string; label: string; maxLength: number };
};

export type LicenceFollowup = {
  startedAt: string;
  firstName: string | null;
  schoolName: string | null;
  documents: PermisDocument[];
  hostDocuments: PermisDocument[];
  hosted: boolean | null;
  survey: { definition: SurveyDefinition; answeredAt: string | null };
  reviews: { school: string | null; permio: string | null };
};

/** Parcours de l'élève, ou `null` (pas de parcours, réponse inattendue) : l'onglet reste caché. */
export function licenceFollowupOf(data: unknown): LicenceFollowup | null {
  if (!data || typeof data !== 'object') return null;
  const followup = (data as { followup?: unknown }).followup as Partial<LicenceFollowup> | null | undefined;
  if (!followup || typeof followup !== 'object') return null;
  if (!Array.isArray(followup.documents) || !Array.isArray(followup.hostDocuments) || !followup.survey?.definition || !followup.reviews)
    return null;
  return followup as LicenceFollowup;
}

/** Lien d'avis ouvrable : https seulement (défense en profondeur, le serveur filtre déjà). */
export function safeReviewUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

export type SurveyDraft = {
  ratings: Record<string, number>;
  recommend: boolean | null;
  comment: string;
};

export const emptySurvey = (): SurveyDraft => ({ ratings: {}, recommend: null, comment: '' });

/** Envoi possible : chaque note donnée (1 à 5), la recommandation choisie, commentaire pas trop long. */
export function surveyReady(definition: SurveyDefinition, draft: SurveyDraft): boolean {
  return (
    definition.ratings.every((r) => Number.isInteger(draft.ratings[r.key]) && draft.ratings[r.key] >= 1 && draft.ratings[r.key] <= 5) &&
    draft.recommend !== null &&
    draft.comment.trim().length <= definition.comment.maxLength
  );
}

/** Réponses envoyées au serveur ; le commentaire vide n'est pas envoyé. */
export function surveyPayload(definition: SurveyDefinition, draft: SurveyDraft): Record<string, number | boolean | string> {
  const answers: Record<string, number | boolean | string> = {};
  for (const r of definition.ratings) answers[r.key] = draft.ratings[r.key];
  answers[definition.recommend.key] = draft.recommend === true;
  const comment = draft.comment.trim();
  if (comment) answers[definition.comment.key] = comment;
  return answers;
}

/** Le rappel « Félicitations pour votre permis ! » reporté par l'élève (« Plus tard ») revient au bout de 7 jours. */
export const REMINDER_SNOOZE_DAYS = 7;

/**
 * Rappel reporté : « Plus tard » choisi il y a moins de 7 jours. Une date illisible ou à venir
 * (horloge changée) ne masque rien : le rappel reste affiché.
 */
export function reminderSnoozed(snoozedAt: string | null | undefined, now: Date): boolean {
  if (!snoozedAt) return false;
  const at = Date.parse(snoozedAt);
  if (!Number.isFinite(at) || at > now.getTime()) return false;
  return now.getTime() - at < REMINDER_SNOOZE_DAYS * 86_400_000;
}

/** Clé du navigateur où le report du rappel est mémorisé, par élève. */
export const reminderSnoozeKey = (candidateId: string) => `pmd-candidat-permis-plus-tard:${candidateId}`;
